<template>
  <div
    class="wt2-asset-gallery wt2-sg"
    :class="{ '-compact-ui': isUICompact }"
    v-show="visible"
  >
    <aside :class="[sidebarState]">
      <div class="-inner filter">
        <filter-assets @search="searchAssets" />
      </div>
      <div class="-inner story">
        <template v-if="sidebarState === 'story'">
          <i
            @click="closeSideBar(true)"
            class="wticon wticon-close_x -close-story"
            title="Hide"
          />
        </template>
      </div>
    </aside>

    <div class="-main" v-el-resize="onMainElResize" ref="mainPanel">
      <div class="-search">
        <div class="-left">
          <!--tbd-->
        </div>
        <div class="-middle">
          <!--
          <wt-instagram-auto-complete
            v-if="isInstagram"
            v-model="$store.state.assetGallery.searchPattern"
            ref="autoComplete"
            :acPromise="acPromise"
            :searchEntity="searchEntity"
            @clear="clearEntity"
            @search="search"
            :placeholder="contextObject.placeholder"
          />
        -->
          <wt-twitter-auto-complete
            v-if="isTwitter"
            v-model="$store.state.assetGallery.searchPattern"
            ref="autoComplete"
            :acPromise="acPromise"
            @clearHistory="clearHistory"
            @clear="abortActiveRequests"
            @search="search"
            :localHistory="localHistory"
            :placeholder="contextObject.placeholder"
            :query-required="contextObject.queryRequired"
            :disable-input="providerNotLoggedIn"
          />
          <wt-auto-complete
            v-else
            v-model="$store.state.assetGallery.searchPattern"
            ref="autoComplete"
            :acPromise="acPromise"
            @clearHistory="clearHistory"
            @clear="abortActiveRequests"
            @search="search"
            :localHistory="localHistory"
            :placeholder="contextObject.placeholder"
            :query-required="contextObject.queryRequired"
          />
        </div>
        <div class="-right">
          <wt-user-assets-actions
            @open-infographics="openInfographics"
            @select-tab="selectTab"
            @open-file="triggerFileOpenDialog"
            :isUICompact="isUICompact"
            :closeModal="closeModal"
          />
        </div>
      </div>

      <wt-files-dropzone :show="isDragging" ref="fileDrop">
        <template slot="indicator">
          <i class="wticon wticon-upload2" />
          <span class="-drop-text">{{
            'generic.DND_INVITATION_M1' | culture
          }}</span>
          <span class="-drop-formats">{{
            'generic.DND_INVITATION_M2' | culture
          }}</span>
        </template>
      </wt-files-dropzone>

      <div class="-tab-bar">
        <div class="-left">
          <wt-icon-with-text
            class="-toggle-filter"
            :class="{ '-compact': !!sidebarState }"
            icon="wticon-Filter_v8"
            text="Filter"
            :active="isFilterOpen"
            :disabled="!canFilter"
            :show-text="!sidebarState"
            :show-badge="canFilter && !hasDefaultContextFilters"
            :title="isFilterOpen ? 'Hide Filters' : 'Show Filters'"
            @click.native="toggleSideBar('filter')"
          />

          <transition name="wt2-fade">
            <wt-radio-text
              v-if="showFormatSelector"
              class="-format-selector -inline"
              :value="contextObject.props.format"
              :full="true"
              :buttons="[
                { text: 'All', title: 'Videos and Images', value: null },
                { text: 'Videos', icon: 'wticon-camera', value: 'VIDEO' },
                { text: 'Images', icon: 'wticon-picture-sun', value: 'IMAGE' },
              ]"
              @select="changeFormat"
            />
          </transition>
        </div>

        <wt-tabs
          class="-tabs"
          :key="displayedAssetGalleryTabs.length"
          type="horizontal bottom"
          :current-tab-id="searchContextId"
          :tabs="displayedAssetGalleryTabs"
          @tab-selected="onTabSelected"
        >
          <template slot="tab" slot-scope="x">
            <template v-if="x.tab.id === 'MY_GALLERY'">
              <wt-svg-animation
                name="upload-folder"
                :play="animate.uploading"
              />
              <button
                class="wt2-tabs-menu-tab-button reserve-space"
                :data-text="x.tab.label"
                :disabled="x.tab.disabled"
              >
                {{ x.tab.label }}
              </button>
            </template>
            <template v-else>
              <span v-if="x.tab.icon" class="wticon" :class="x.tab.icon" />
              <button
                class="wt2-tabs-menu-tab-button reserve-space"
                :data-text="x.tab.label"
                :disabled="x.tab.disabled"
              >
                {{ x.tab.label }}
              </button>
            </template>
          </template>
        </wt-tabs>

        <div class="-right">
          <div
            class="wt2-link -buzz-toggle"
            v-show="
              !isMyGallery &&
              !isKaltura &&
              !isGif &&
              !isImpededTemplatizer &&
              !shouldShowBuzz &&
              !isInFavoritesTab &&
              !isWochitOwnedTab
            "
            :class="{ disabled: manualTagsDisabled }"
            @click="toggleManualShowBuzz"
          >
            <span
              v-if="manualTagsDisabled"
              class="disabled-tooltip"
              v-tooltip="{
                title: 'Currently not available',
                container: '.disabled-tooltip',
                placement: 'top auto',
                delay: { show: 0, hide: 0 },
              }"
            />
            <span class="wticon wticon-buzz-lightning" />
            <!-- <span v-if="!sidebarState">Buzz</span> -->
            <span>Buzz</span>
          </div>

          <img
            v-show="isGif"
            class="-powered-by-klipy"
            src="/resources/img/powered-by-klipy.png"
            alt="Powered by KLIPY"
          />

          <wt-dropdown
            type="normal right"
            v-if="showSortDropdown"
            class="-sort-by"
            icon="wticon-sorting"
            :show-label="!sidebarState"
            :items="sortOptions"
            :current-item-id="currentSort"
            :on-click-item="setSortBy"
          ></wt-dropdown>
          <wt-toggle
            :key="searchContextId"
            v-if="isMyGallery && !isEmbedded"
            :checked="myUploadsOnly"
            @toggle="toggleMyUploads"
            label-left="My Uploads"
          />
          <wt-toggle
            :key="searchContextId"
            v-if="showMyFavoritesToggle"
            :checked="myFavoritesOnly"
            @toggle="toggleMyFavorite"
            label-left="My Favorites"
          />
        </div>
      </div>

      <div class="-buzz">
        <manual-tags
          :showTags="shouldShowBuzz"
          :selectedTag="manualTagsHighlight"
          :is-content-marketing="isContentMarketing"
          @select="onManualTagSelected"
          @tags-disabled="manualTagsDisabled = true"
          @hide="toggleManualShowBuzz"
        >
        </manual-tags>
      </div>

      <div class="-tags" v-if="showTags">
        <search-tags
          :showTags="!shouldShowBuzz"
          :rawTags="searchTags"
          :selectedTags="selectedTags"
          @change="onSearchTagsSelected"
        >
        </search-tags>
      </div>
      <div
        class="-twitter-disabled wt2-gallery-assets-empty-pages"
        v-if="isTwitter"
      >
        <div class="-empty-pages-box">
          <div class="-description" style="text-align: center">
            Due to recent changes in the Twitter API policy, this service is
            temporarily unavailable.<br />
            We apologize for the inconvenience and are working on a solution.<br />
            Thank you for your understanding.
          </div>
        </div>
      </div>
      <empty-pages
        v-else-if="!inProgress && !hasDisplayedAssets"
        ref="emptyPages"
        :has-error="searchError"
        :has-search="!!(queryText && queryText.length)"
        :has-filters="!hasDefaultContextFilters"
        :is-provider-not-logged-in="providerNotLoggedIn"
        :search-context-id="searchContextId"
        :has-entity="hasEntity"
        @open-file="triggerFileOpenDialog"
        @set-provider-status="setProviderNotLoggedInStatus"
        @retry="searchAssets"
      />
      <scroller
        v-else
        ref="galleryContainer"
        class="-scroll-container"
        :items="assetsToDisplay"
        :key="searchContextId"
        key-field="_id"
        @fetch-more="getMoreAssets"
        :getDimensions="getDimensions"
      >
        <template v-slot="{ item }">
          <wt-gallery-asset
            v-if="item._uploading"
            :uploading="true"
            :asset="item"
            :key="item._id"
            @upload-done="GALLERY_UPLOAD_DONE(item)"
          />
          <wt-gallery-asset
            v-else
            :asset="item"
            :key="item._id"
            @preview-asset="previewAsset"
          />
        </template>
      </scroller>

      <div v-show="inProgress" class="wt-loader" />

      <input
        v-show="false"
        type="file"
        ref="file"
        @change="onFileChange"
        :accept="$options.mimeTypes"
        multiple
      />
    </div>
  </div>
</template>

<script>
import app from 'app';
import SearchTags from 'v/mediaFiltering/SearchTags.vue';
import ManualTags from 'v/mediaFiltering/ManualTags.vue';
import EmptyPages from 'v/mediaFiltering/EmptyPages.vue';
import FilterAssets from 'v/mediaFiltering/filters/FilterAssets.vue';
import InfogfxModal from 'v/InfogfxModal/InfogfxModal';
import FileUpload from 'v/UploadView/FileUpload.vue';
import Scroller from '@wochitnpm/recyclescroller';
import AssetModal from 'v/assetModal/AssetModal.vue';
import AssetEditorStore from 'AssetEditor/store/AssetEditorStore';
import { mapGetters, mapState, mapActions } from 'vuex';

export default {
  name: 'wt-asset-gallery',
  components: {
    'search-tags': SearchTags,
    'manual-tags': ManualTags,
    'filter-assets': FilterAssets,
    'empty-pages': EmptyPages,
    scroller: Scroller,
  },
  store() {
    return app.rootStore;
  },

  analytics: {
    showBuzz: 'Show Buzz',
    showFilterBar: 'Show Filter Bar',
    showStoryBar: 'Show Story Bar',
    tabSelected: 'Tab Selected in Media Selection',
    search: 'Assets Searched',
    clearSearch: 'Clear Search',
    hideStoryBar: 'Hide Story Bar',
  },

  props: {
    closeModal: {
      type: Function,
      required: false,
      default: () => {},
    },
  },

  data() {
    return {
      guid: app.guid(),
      isEmbedded: app.isEmbedded,
      newScroll: 1,
      animate: {
        filter: false,
        uploading: !app.UploadQueue.getVisualQue().isEmpty(),
      },
      isDragging: false,
      isUICompact: false,
      visible: false,
      manualShowBuzz: false,
      showBuzzWithQueryText: false,
      showTags: false,
      acPromise: () => {},
      localHistory: [],
      providerNotLoggedIn: false,
      searchError: false,
      observer: null,
      manualTagsDisabled: false,
    };
  },

  computed: {
    ...mapGetters('user', ['isImpededTemplatizer']),
    ...mapGetters([
      'contextObject',
      'displayedAssetGalleryTabs',
      'assetsSource',
      'hasDefaultContextFilters',
      'selectedAssets',
    ]),
    ...mapState({
      allowedProviders: (state) =>
        state.assetGallery.searchContexts[app.PROVIDERS.WOCHIT.id].filters
          .providers,
      uploadingAssetsToDisplay: (state) =>
        state.assetGallery.galleryUploadingAssets,
      assetSearchResults: (state) => state.assetGallery.assetSearchResults,
      noMoreAssets: (state) => state.assetGallery.noMoreAssets,
      searchTags: (state) => state.assetGallery.searchTags,
      searchPattern: (state) => state.assetGallery.searchPattern,
      selectedTags: (state) =>
        state.assetGallery.searchContexts[state.assetGallery.searchContextId]
          .selectedTags,
      inProgress: (state) => state.assetGallery.searchInProgress,
      searchContextId: (state) => state.assetGallery.searchContextId,
      sidebarState: (state) => state.assetGallery.sidebarState,
      queryText: (state) =>
        state.assetGallery.searchContexts[state.assetGallery.searchContextId]
          .query.text,
      searchEntity: (state) =>
        state.assetGallery.searchContexts[state.assetGallery.searchContextId]
          .entity,
      canFilter: (state) =>
        _.isDefined(
          state.assetGallery.searchContexts[state.assetGallery.searchContextId]
            .filters
        ),
      sortProps: (state) =>
        state.assetGallery.sortContext[state.assetGallery.searchContextId],
      myUploadsOnly: (state) => state.assetGallery.myUploadsOnly,
      myFavoritesOnly: (state) => state.assetGallery.myFavoritesOnly,
      searchFailedError: (state) => state.assetGallery.searchFailedError,
    }),

    canSearch() {
      return !(this.contextObject.queryRequired && _.isEmpty(this.queryText));
    },

    isInstagram() {
      return this.searchContextId === app.PROVIDERS.INSTAGRAM.id;
    },

    isCreative() {
      return this.searchContextId === app.PROVIDERS.WOCHIT_CREATIVE.id;
    },

    isTwitter() {
      return this.searchContextId === app.PROVIDERS.TWITTER.id;
    },

    isMyGallery() {
      return this.searchContextId === app.PROVIDERS.MY_GALLERY.id;
    },

    isKaltura() {
      return this.searchContextId === app.PROVIDERS.KALTURA.id;
    },

    isGif() {
      return this.searchContextId === app.PROVIDERS.GIF.id;
    },

    isWochitOwnedTab() {
      return this.searchContextId === app.PROVIDERS.WOCHIT_OWNED_ASSETS.id;
    },

    isInFavoritesTab() {
      return this.searchContextId === app.PROVIDERS.FAVORITES.id;
    },

    showMyFavoritesToggle() {
      return this.isInFavoritesTab && !app.isEmbedded;
    },

    isSocial() {
      return this.isInstagram || this.isTwitter;
    },

    isWochit() {
      return this.searchContextId === app.PROVIDERS.WOCHIT.id;
    },

    isFilterOpen() {
      return this.sidebarState === 'filter';
    },

    showFormatSelector() {
      return this.contextObject.showFormatSelector;
    },

    hasEntity() {
      return _.isPlainObject(this.contextObject.entity);
    },

    currentSort() {
      return this.contextObject.sortBy;
    },

    hasDisplayedAssets() {
      return !!this.assetsToDisplay.length;
    },

    assetsToDisplay() {
      if (this._isDestroyed) {
        return [];
      }
      const aList = this.isInFavoritesTab
        ? this.assetSearchResults.filter(
            (asset) => !!asset.metadata.favoriteOwner
          )
        : this.assetSearchResults;
      const selectedIds = _.map(this.selectedAssets, (asset) => {
        return asset._providerId ? asset._providerId : asset.metadata.assetUUID;
      });

      // sometimes selected assets and assetSearchResults go out sync
      // like: pinned, so we merge shallow with search result
      let uploading = [];
      if (this.isMyGallery && !this.inProgress) {
        uploading = _.map(this.uploadingAssetsToDisplay, (a) => {
          return {
            _uploading: true,
            _id: a.metadata.assetUUID || a.id || a._providerId,
            ...a,
          };
        });
      }
      const assets = _.map(aList, (asset) => {
        const aId = asset.metadata.assetUUID || asset.id || asset._providerId;
        if (aId && selectedIds.includes(aId)) {
          const selectedAsset = _.find(this.selectedAssets, (sAsset) => {
            const id =
              sAsset._providerId || sAsset.metadata.assetUUID || sAsset.id;
            return id === aId;
          });
          return Object.assign({}, asset, {
            _id: aId,
            pinned: selectedAsset.pinned,
          });
        }
        return { ...asset, _id: aId };
      });

      return [...uploading, ...assets];
    },

    manualTagsHighlight() {
      return (
        this.queryText ||
        (this.selectedTags && this.selectedTags[0]
          ? this.selectedTags[0][0]
          : null)
      );
    },

    shouldShowBuzz() {
      let show = false;

      if (
        this.isMyGallery ||
        this.isKaltura ||
        this.isGif ||
        this.manualTagsDisabled ||
        this.isInFavoritesTab ||
        this.isWochitOwnedTab
      ) {
        show = false;
      } else if (this.manualShowBuzz && this.showBuzzWithQueryText) {
        show = true;
      } else if (
        this.queryText.length ||
        (this.selectedTags && this.selectedTags.length)
      ) {
        show = false;
      }

      return show;
    },

    searchContextIdLowerCase() {
      return (this.searchContextId || '').toLowerCase();
    },

    showSortDropdown() {
      return this.sortOptions.length > 1;
    },

    sortOptions() {
      let options = this.sortProps && this.sortProps.options;
      if (!options) {
        return [];
      }
      if (!this.searchPattern.trim()) {
        // this will remove the best match option
        // because the best match id is null
        if (this.isCreative) {
          options = options.filter((o) => o.id !== 'RELEVANCE');
        } else {
          options = options.filter((o) => o.id);
        }
      }
      return options;
    },

    isContentMarketing() {
      return _.isEmpty(this.allowedProviders);
    },
  },

  watch: {
    isInstagram: {
      immediate: true,
      handler() {
        this.acPromise = this.getAutoCompletePromise();
      },
    },
    queryText: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length) {
          this.showBuzzWithQueryText = false;
        } else if (!this.selectedTags || !this.selectedTags.length) {
          this.showBuzzWithQueryText = true;
        }
      },
    },
    selectedTags: {
      immediate: true,
      handler(newTags) {
        if (newTags && newTags.length) {
          this.showBuzzWithQueryText = false;
        } else if (!this.queryText.length) {
          this.showBuzzWithQueryText = true;
        }
      },
    },
    searchContextId() {
      if (this.isTwitter) {
        // if the promise is not resolved before another browser render
        // there will be a flash when switching views, so assigned to null to prevent that
        this.providerNotLoggedIn = null;
        this.setProviderNotLoggedInStatus();
      } else {
        this.providerNotLoggedIn = false;
      }
    },

    searchFailedError(err) {
      if (!err) {
        return;
      }
      if (this.isSocial && [401, 422, 403].includes(err.status)) {
        this.searchError = true;
        // revoked
        if (err.status !== 403) {
          this.$nextTick(() => {
            this.$refs.emptyPages.revokeSocial();
          });
        }
      }
    },
  },

  beforeDestroy() {
    this.$options.autofetchTimer = clearTimeout(this.$options.autofetchTimer);
    app.namespace.Util.FileDndDispatcher.remove(this.guid);
    app.stopListening(
      app.UploadQueue.unfinished,
      'add remove',
      this.$_uploadQueue_changed
    );
    this.CLEAR_STORE();
  },

  created() {
    this.searchAssets({
      resetSelectedTags: this.searchContextId === 'MY_GALLERY',
    });

    app.listenTo(
      app.UploadQueue.unfinished,
      'add remove',
      this.$_uploadQueue_changed
    );

    Object.assign(this.$options, {
      mimeTypes: app.DEFAULT.BROADLY_INCLUSIVE_MEDIA_MIMES,
      supportedFileExtensions: tk.context._supportedFileExtensions,
    });
  },

  mounted() {
    app.namespace.Util.FileDndDispatcher.add({
      guid: this.guid,
      handlers: {
        dragover: this.$_dragOver,
        dragenter: this.$_dragEnter,
        dragleave: this.$_dragLeave,
        drop: this.$_drop,
        paste: this.$_onPaste,
      },
    });
    let localFetch = app.remoteStash.get('searchHistory');
    if (Array.isArray(localFetch)) {
      localFetch = [...localFetch];
    } else {
      localFetch = [];
    }

    if (localFetch.length > 5000) {
      localFetch.shift();
    }

    if (this.$refs.autoComplete) {
      const el = this.$refs.autoComplete.$el;
      if (el) {
        const input = el.querySelector('input');
        if (input) {
          setTimeout(() => {
            input.focus();
          }, 0);
        }
      }
    }

    localFetch = localFetch.map((entry) => ({
      text: entry,
      source: 'history',
    }));
    localFetch = localFetch.reverse();
    this.localHistory = [...localFetch];
  },

  methods: {
    ...mapActions([
      'CLEAR_STORE',
      'SEARCH_ASSETS',
      'GET_MORE_ASSETS',
      'SET_PROGRAMMER',
      'SET_SIDEBAR_STATE',
      'CHANGE_SEARCH_CONTEXT',
      'SET_QUERY',
      'SET_FORMAT',
      'SET_SORT_BY',
      'SET_ASSETS_SOURCE',
      'SET_INSTAGRAM_ENTITY',
      'SET_SELECTED_TAGS',
      'SET_MY_UPLOADS',
      'SET_MY_FAVORITE',
      'GALLERY_UPLOAD_DONE',
    ]),

    getDimensions(el) {
      const { paddingLeft, paddingRight } = getComputedStyle(el);
      const padding = parseInt(paddingLeft) + parseInt(paddingRight);
      const containerWidth = el.clientWidth - padding;
      const baseItemWidth = 225;
      const gap = 15;

      // formula: baseItemWidth * n + gap * (n - 1) = containerWidth;
      // (baseItemWidth + gap) * n = containerWidth + gap

      let n = (containerWidth + gap) / (baseItemWidth + gap);
      n = Math.floor(n);

      const width = Math.floor((containerWidth - (n - 1) * gap) / n);
      let height = Math.round((width * 9) / 16);
      if (this.searchContextId !== 'GIF') {
        height += 60;
      }

      return { width, height, itemsInRow: n, gap };
    },

    selectTab(tabId) {
      this.setAssetsSource(app.assetsSource.NORMAL);
      this.CHANGE_SEARCH_CONTEXT({ id: tabId });
      this.SET_QUERY({ queryText: '', queryValue: '' });
      this.searchAssets();
    },

    $_uploadQueue_changed(/* uqModel, uqCollection, options*/) {
      this.animate.uploading = !app.UploadQueue.getVisualQue().isEmpty();
    },

    onMainElResize: _.debounce(function (el) {
      this.$_recalculateUIMode(el);
    }, 200),

    $_recalculateUIMode(el) {
      if (this._isDestroyed || !el) {
        return;
      }

      const cr = el.getBoundingClientRect();
      this.isUICompact = cr.width <= 1280;
    },

    show() {
      this.visible = true;
    },
    onSearchTagsSelected(selectedTags) {
      this.SET_SELECTED_TAGS(selectedTags);
      this.trackSearch('keyword');
      this.searchAssets();
    },
    abortActiveRequests() {
      app.api.instagramEntities.stopEntityXHR();
      app.api.searchAutoCompleteSuggestions.abort();
    },
    onTabSelected(tab) {
      this.abortActiveRequests();
      const tempValue = this.$refs.autoComplete.tempValue;
      const searchPattern = (this.searchPattern || '').trim();
      const currentContextId = this.searchContextId;
      this.CHANGE_SEARCH_CONTEXT({ id: tab.id, initialValue: tempValue });
      if (tab.id === 'MY_GALLERY') {
        this.$refs.autoComplete.tempValue = '';
      }
      this.$triggerAnalytics('tabSelected', { tab: tab.label });
      if (
        this.isInstagram &&
        this.queryText &&
        (!this.searchEntity ||
          (this.searchEntity && this.searchEntity.text !== this.queryText))
      ) {
        this.$nextTick(() => {
          this.$refs.autoComplete.forceFocus();
        });
      }
      if (this.canSearch) {
        if (!this.isInstagram || this.searchEntity) {
          this.searchAssets({
            resetSelectedTags:
              this.searchContextId !== currentContextId &&
              tempValue !== searchPattern,
          });
        }
      }
    },
    hide() {
      this.visible = false;
    },

    previewAsset(asset) {
      const store = new AssetEditorStore(
        {
          openedFrom: 'asset-gallery',
          currentPageId: null,
          ian: {
            getAssets: () => this.assetsToDisplay,
          },
          timeInAsset: 0,
          isStoryboarder: app.rootStore.getters['user/isTemplateSpecialist'],
        },
        {
          storyModel: app.storyController.story,
          asset,
        }
      );

      app.rootStore.registerModule('assetEditor', store);
      app
        .showModal({
          type: 'custom',
          wrapperClass: ['wt2-asset-modal'],
          closeOnClickOut: false,
          showCloseButton: false,
          component: AssetModal,
          store: app.rootStore,
          componentPropsData: {},
        })
        .$on('close', (e) => {});
    },

    openInfographics(templateId) {
      const infogfxModal = new InfogfxModal({
        model: app.storyController.story,
        infogfxTemplate: templateId,
      });

      this.selectTab(app.PROVIDERS.MY_GALLERY.id);

      app.controller.showViewInModal(infogfxModal);

      infogfxModal.on('infogfxGallery:newAsset', (asset) => {
        this.$store.dispatch('ESCORT_UPLOADING_ASSET', {
          uploadDestination: app.uploadDestination.ASSET_GALLERY,
          asset,
        });
      });
    },

    toggleSideBar(mode) {
      if (mode === 'filter') {
        if (this.sidebarState !== 'filter') {
          this.$triggerAnalytics('showFilterBar');
        } else {
          this.$triggerAnalytics('hideFilterBar', { from: 'filter button' });
        }
      }
      if (mode === 'story') {
        if (this.sidebarState !== 'story') {
          this.$triggerAnalytics('showStoryBar');
        } else {
          this.$triggerAnalytics('hideStoryBar', { from: 'story button' });
        }
      }
      this.SET_SIDEBAR_STATE(this.sidebarState === mode ? null : mode);

      this.$nextTick(() => {
        this.$_recalculateUIMode(this.$refs.mainPanel);
      });
    },
    closeSideBar(track) {
      if (track) {
        this.$triggerAnalytics('hideStoryBar', { from: 'close button' });
      }
      this.SET_SIDEBAR_STATE(null);
    },
    changeFormat(newFormat) {
      this.SET_FORMAT(newFormat);
      this.searchAssets();
    },
    clearHistory() {
      this.localHistory = [];
      app.remoteStash.set('searchHistory', null);
    },
    clearEntity() {
      this.SET_INSTAGRAM_ENTITY(null);
    },

    search: function ({ entity, e, activeRowIndex } = {}) {
      if (e) {
        e.preventDefault();
      }

      const searchPattern = (this.searchPattern || '').trim();

      if (this.isInstagram) {
        // && entity
        console.log({
          queryText: searchPattern,
          queryValue: searchPattern,
        });
        let instagramSearchPattern = searchPattern;
        if (instagramSearchPattern.startsWith('@')) {
          instagramSearchPattern = searchPattern.slice(1);
        }
        this.SET_QUERY({
          queryText: searchPattern,
          queryValue: instagramSearchPattern,
        });
      } else {
        this.SET_QUERY({
          queryText: searchPattern,
          queryValue: searchPattern,
        });
        this.clearEntity();
        if (
          !_.isEmpty(searchPattern) &&
          searchPattern.length <= 150 &&
          !this.isInstagram
        ) {
          let localFetch = app.remoteStash.get('searchHistory');
          if (Array.isArray(localFetch)) {
            localFetch = [...localFetch];
          } else {
            localFetch = [];
          }

          if (localFetch.length > 5000) {
            localFetch.shift();
          }

          if (!localFetch.includes(searchPattern)) {
            localFetch.push(searchPattern);
          }

          app.remoteStash.set('searchHistory', localFetch);
          localFetch = localFetch.map((entry) => ({
            text: entry,
            source: 'history',
          }));
          localFetch = localFetch.reverse();
          this.localHistory = [...localFetch];
        }
      }

      let event;
      let using;

      if (e instanceof jQuery.Event) {
        event = e.originalEvent;
      } else {
        event = e;
      }

      if (event instanceof KeyboardEvent) {
        if (!activeRowIndex && activeRowIndex !== 0) {
          using = 'enter key';
        } else {
          using = 'auto complete using keyboard';
        }
      } else if (event instanceof MouseEvent) {
        if (event.currentTarget.classList.contains('-search')) {
          using = 'search button clicked';
        } else {
          if (!event.currentTarget.classList.contains('-clear')) {
            using = 'auto complete using mouse';
          } else {
            this.$triggerAnalytics('clearSearch'); // This is wjy
          }
        }
      }

      if (!_.isEmpty(searchPattern)) {
        this.trackSearch(using);
      }

      this.searchAssets({ resetSelectedTags: true });
    },

    retrySearch() {
      this.searchAssets({ resetSelectedTags: true });
    },

    searchAssets(options) {
      this.searchError = false;
      this.SEARCH_ASSETS(options).then(() => {
        if (this.hasDisplayedAssets) {
          this.getMoreIfNoScrollbar();
        }
      });
    },

    getMoreAssets() {
      return this.GET_MORE_ASSETS();
    },

    setProviderNotLoggedInStatus() {
      app.api.socialProvider.getUserAccounts().done((accounts) => {
        this.providerNotLoggedIn = _.isUndefined(
          accounts[this.searchContextIdLowerCase]
        );
      });
    },

    getAutoCompletePromise() {
      if (this.isInstagram) {
        return (query) => {
          query = query.replace(/["']/g, '').trim().toLowerCase();
          const data = {
            query: query,
            contentFamily: 'Editorial_and_Creative',
          };
          return app.api.instagramEntities.get(data);
        };
      }
      return (query, filterOut) => {
        query = query.replace(/["']/g, '').trim().toLowerCase();
        const data = {
          q: query,
          contentFamily: 'Editorial_and_Creative',
        };
        return app.api.searchAutoCompleteSuggestions.get(data, filterOut);
      };
    },

    setSortBy(sortBy) {
      this.SET_SORT_BY(sortBy);
      this.searchAssets();
    },

    setAssetsSource(assetSource) {
      this.SET_ASSETS_SOURCE({ source: assetSource });
      this.searchAssets();
    },

    onManualTagSelected(tag) {
      if (tag.type === 'KEYWORD') {
        if (!this.isWochit) {
          this.CHANGE_SEARCH_CONTEXT({ id: app.PROVIDERS.WOCHIT.id });
        }
        this.SET_QUERY({ queryText: '', queryValue: '' });
        this.SET_SELECTED_TAGS([[tag.phrase]]);
        this.trackSearch('buzz');
        this.searchAssets();
      } else {
        this.SET_QUERY({ queryText: tag.phrase, queryValue: tag.phrase });
        this.SET_SELECTED_TAGS([]);
        if (this.isInstagram) {
          this.$nextTick(() => {
            this.$refs.autoComplete.forceFocus();
          });
        } else {
          this.trackSearch('buzz');
          this.searchAssets();
        }
      }
    },

    trackSearch(using) {
      const format = this.contextObject.props.format || 'ALL';
      const tab = this.searchContextId;
      this.$triggerAnalytics('search', { using, format, tab });
    },

    toggleManualShowBuzz() {
      if (this.manualTagsDisabled) {
        return;
      }

      if (this.queryText.length && !this.showBuzzWithQueryText) {
        this.showBuzzWithQueryText = this.manualShowBuzz = true;
      } else {
        this.manualShowBuzz = !this.manualShowBuzz;
      }

      if (this.manualShowBuzz) {
        this.$triggerAnalytics('showBuzz');
      }
    },

    toggleMyUploads(newValue) {
      this.SET_MY_UPLOADS(newValue);
      this.searchAssets();
    },
    toggleMyFavorite(newValue) {
      this.SET_MY_FAVORITE(newValue);
      this.searchAssets();
    },

    onContinuousPaginationMounted({ observer }) {
      this.observer = observer;
    },

    triggerFileOpenDialog() {
      this.$refs.file.value = null;
      this.$refs.file.click();
    },

    onFileChange(e) {
      this.openFileUpload(e, {
        analyticsData: {
          using: 'button',
          'from screen': 'media selection',
        },
      });
    },

    $_onPaste(e) {
      this.openFileUpload(e, {
        analyticsData: {
          using: 'copy/paste',
          'from screen': 'media selection',
        },
      });
    },

    openFileUpload(e, options) {
      options = Object.assign(
        {
          analyticsData: null,
        },
        options
      );

      const fileList = app.getFilesFromEvent(e);
      if (!fileList.length) {
        return;
      }

      this.mixinShowViewInModal({
        type: 'normal',
        store: app.rootStore,
        component: FileUpload,
        componentPropsData: {
          analyticsData: options.analyticsData,
          validateVideoDuration: false,
          initEvent: e,
          allowAudioFiles: !app.rootStore.getters['user/isImpededTemplatizer'],
        },
        backdrop: true,
        closeOnClickOut: false,
      }).$on('close', this.$_processFileUploadResult);
    },

    getMoreIfNoScrollbar(i) {
      if (!_.isNumber(i)) {
        i = 0;
      }

      if (!this.canSearch || this._isDestroyed || i === 10) {
        // this serves us to protect from a situation where this.noMoreAssets is not updated appropriately,
        // causing infinite promise call and browser freeze
        return;
      }

      const el = this.$refs.galleryContainer.$el;
      if (!this.noMoreAssets && el.clientHeight >= el.scrollHeight) {
        const promise = this.getMoreAssets();
        if (promise) {
          promise.then(() => {
            if (!this._isDestroyed) {
              this.$options.autofetchTimer = setTimeout(
                this.getMoreIfNoScrollbar.bind(this, i + 1),
                1000
              );
            }
          });
        }
      }
    },

    $_dragOver(e) {
      const dt = e.dataTransfer;
      if (dt.types && dt.types.indexOf('Files') >= 0) {
        e.preventDefault();
        e.stopPropagation();
        dt.dropEffect = 'copy';
      } else {
        this.isDragging = false;
      }
    },

    $_dragEnter: function (e) {
      e.preventDefault();
      if (e.fromElement === null && !app.isEmbedded) {
        this.isDragging = true;
        this.SET_SIDEBAR_STATE(null);
      }
    },

    $_dragLeave: function (e) {
      e.preventDefault();
      if (e.fromElement === null) {
        this.isDragging = false;
      }
    },

    $_drop: function (e) {
      e.preventDefault();
      this.isDragging = false;

      if (this.visible && this.$refs.fileDrop.active) {
        this.mixinShowViewInModal({
          type: 'normal',
          store: app.rootStore,
          component: FileUpload,
          componentPropsData: {
            analyticsData: {
              using: 'drag',
              'from screen': 'media selection',
            },
            validateVideoDuration: false,
            initEvent: e,
            allowAudioFiles:
              !app.rootStore.getters['user/isImpededTemplatizer'],
          },
          backdrop: true,
          closeOnClickOut: false,
        }).$on('close', this.$_processFileUploadResult);
      }
    },

    $_processFileUploadResult(res) {
      if (res.uqModels) {
        let hadNarration = false;
        let hadVisual = false;

        _.each(res.uqModels, (model) => {
          const isNarration =
            model.options.uploadType === app.uploadType.NARRATION_ASSET;
          const uploadDestination = isNarration
            ? app.uploadDestination.LIGHTBOX
            : app.uploadDestination.ASSET_GALLERY;
          hadNarration = hadNarration || isNarration;
          hadVisual = hadVisual || !isNarration;

          app.rootStore.dispatch('ESCORT_UPLOADING_ASSET', {
            isNarration,
            uploadDestination,
            asset: model.attributes,
          });
        });

        if (hadNarration) {
          app.notificationController.push({
            type: 'INFO',
            text: app.culture.get('uploadView.audioInBackgroundInfo'),
            timeout: app.TIME.second(2),
            uniqueGroupName: 'AUDIO_UPLOADED_FROM_GALLERY',
          });
        }

        if (hadVisual) {
          this.selectTab(app.PROVIDERS.MY_GALLERY.id);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/deep/ input.wt-input {
  &:focus::-webkit-input-placeholder {
    //transition: opacity 0.5s 1s ease;
    color: rgba(0, 0, 0, 0.5);
    //opacity: 0;
  }
}

.-twitter-disabled {
}
</style>
